import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import type { BillDTO, CreateBillRequest, CreateBillsRequest } from '@six-pockets/shared';
import { billsApi } from '@/api';
import { useTableActions } from './TablesContext';

interface BillsContextValue {
  /** Creates the bill and updates every table it touched. Throws on failure so the form can show the error. */
  createBill: (body: CreateBillRequest) => Promise<BillDTO>;
  /** Creates several bills together, all or none (a booking split between teams). Throws on failure. */
  createBills: (body: CreateBillsRequest) => Promise<BillDTO[]>;
}

const BillsContext = createContext<BillsContextValue | null>(null);

export function BillsProvider({ children }: { children: ReactNode }) {
  const { replaceTables, refreshOpenSales } = useTableActions();

  const createBill = useCallback(
    async (body: CreateBillRequest) => {
      const { bill, tables } = await billsApi.create(body);
      replaceTables(tables); // also drops the billed bookings from the selection
      void refreshOpenSales();
      return bill;
    },
    [replaceTables, refreshOpenSales],
  );

  const createBills = useCallback(
    async (body: CreateBillsRequest) => {
      const { bills, tables } = await billsApi.createBatch(body);
      replaceTables(tables);
      void refreshOpenSales();
      return bills;
    },
    [replaceTables, refreshOpenSales],
  );

  const value = useMemo(() => ({ createBill, createBills }), [createBill, createBills]);
  return <BillsContext.Provider value={value}>{children}</BillsContext.Provider>;
}

export function useBills(): BillsContextValue {
  const ctx = useContext(BillsContext);
  if (!ctx) throw new Error('useBills must be used inside <BillsProvider>');
  return ctx;
}
